'use client';

import React from 'react';
import Link from 'next/link';
import { AlertTriangle, Clock, CreditCard, XCircle } from 'lucide-react';
import type { SubscriptionStatus } from '@/lib/billing';
import { trialDaysRemaining } from '@/lib/billing';
import { useTimezone } from '@/hooks/useTimezone';

interface SubscriptionBannerProps {
  status: SubscriptionStatus;
  trialEndsAt: string | null;
  declaredBeds: number;
  declaredClients: number;
  actualBeds: number | null;
  actualClients: number | null;
  isComplianceTrial?: boolean;
  canManageBilling?: boolean;
}

export function SubscriptionBanner({
  status,
  trialEndsAt,
  declaredBeds,
  declaredClients,
  actualBeds,
  actualClients,
  isComplianceTrial = false,
  canManageBilling = false,
}: SubscriptionBannerProps) {
  const tz = useTimezone();
  const daysLeft = trialDaysRemaining(trialEndsAt);
  const countsUnavailable = actualBeds === null || actualClients === null;
  const bedsOver = actualBeds !== null && actualBeds > declaredBeds && declaredBeds > 0;
  const clientsOver =
    actualClients !== null && actualClients > declaredClients && declaredClients > 0;

  // Nothing to show for active subscribers within limits
  if (status === 'active' && !countsUnavailable && !bedsOver && !clientsOver) return null;

  // Trial banner
  if (status === 'trialing' && daysLeft !== null) {
    const urgent = daysLeft <= 3;
    return (
      <div
        className={`px-4 py-3 text-body flex items-center justify-between ${
          urgent
            ? 'bg-amber-50 border-b border-amber-200 text-amber-800'
            : 'bg-blue-50 border-b border-blue-200 text-blue-800'
        }`}
      >
        <div className="flex items-center gap-2">
          <Clock className="w-4 h-4 flex-shrink-0" />
          <span>
            Your trial ends on{' '}
            {new Date(trialEndsAt!).toLocaleDateString('en-AU', {
              timeZone: tz,
              day: 'numeric',
              month: 'long',
              year: 'numeric',
            })}
            {daysLeft > 0 && ` (${daysLeft} day${daysLeft === 1 ? '' : 's'} remaining)`}.{' '}
            {isComplianceTrial
              ? 'The compliance workflows included in your trial are available until then.'
              : 'All modules are available during your trial.'}
          </span>
        </div>
        <Link
          href={isComplianceTrial ? '/help' : '/settings/billing'}
          className="font-semibold hover:underline flex-shrink-0 ml-4"
        >
          {isComplianceTrial ? 'Contact Statura' : 'Subscribe now'}
        </Link>
      </div>
    );
  }

  // Past due / payment failed
  if (status === 'past_due' || status === 'incomplete' || status === 'unpaid') {
    return (
      <div className="px-4 py-3 text-body flex items-center justify-between bg-status-danger-bg border-b border-status-danger-border text-status-danger-fg">
        <div className="flex items-center gap-2">
          <CreditCard className="w-4 h-4 flex-shrink-0" />
          <span>
            Your payment has failed. Please update your payment method to avoid service
            interruption.
          </span>
        </div>
        <Link href="/settings/billing" className="font-semibold hover:underline flex-shrink-0 ml-4">
          Update payment
        </Link>
      </div>
    );
  }

  // Canceled / suspended / trial_expired (Phase 5.7 P1-C)
  if (status === 'canceled' || status === 'suspended' || status === 'trial_expired') {
    const message =
      status === 'trial_expired'
        ? 'Your free trial has ended. Subscribe to continue using the platform.'
        : 'Your subscription has been cancelled. Subscribe to continue using the platform.';
    return (
      <div className="px-4 py-3 text-body flex items-center justify-between bg-status-danger-bg border-b border-status-danger-border text-status-danger-fg">
        <div className="flex items-center gap-2">
          <XCircle className="w-4 h-4 flex-shrink-0" />
          <span>{message}</span>
        </div>
        <Link href="/settings/billing" className="font-semibold hover:underline flex-shrink-0 ml-4">
          Subscribe
        </Link>
      </div>
    );
  }

  if (countsUnavailable) {
    if (!canManageBilling) return null;
    return (
      <div
        role="status"
        className="border-b border-status-warning-border bg-status-warning-bg px-4 py-3 text-body text-status-warning-fg"
      >
        Subscription usage unavailable. Active bed or client counts could not be loaded. Refresh or
        ask your administrator to review usage.
      </div>
    );
  }

  // Over declared limits (soft enforcement)
  if (bedsOver || clientsOver) {
    const parts: string[] = [];
    if (bedsOver) parts.push(`${actualBeds} active beds (subscribed for ${declaredBeds})`);
    if (clientsOver)
      parts.push(`${actualClients} active clients (subscribed for ${declaredClients})`);

    return (
      <div className="px-4 py-3 text-body flex items-center justify-between bg-status-warning-bg border-b border-status-warning-border text-status-warning-fg">
        <div className="flex items-center gap-2">
          <AlertTriangle className="w-4 h-4 flex-shrink-0" />
          <span>
            You have {parts.join(' and ')}. Please update your subscription to match your actual
            usage.
          </span>
        </div>
        <Link href="/settings/billing" className="font-semibold hover:underline flex-shrink-0 ml-4">
          Update quantity
        </Link>
      </div>
    );
  }

  return null;
}
